import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { HardHat } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CmsImageProps {
    src: string | null | undefined
    alt: string | null | undefined
    sizes: string
    className?: string
    preload?: boolean
}

// Images on the site's own storage CDN (allowed in next.config.ts) are resized
// and optimized; any other address is shown as-is so a pasted URL never breaks a page.
const OPTIMIZED_HOST = /\.cdn\.modulify\.website$/i

const canOptimize = (src: string): boolean => {
    if (src.startsWith('/')) return !src.startsWith('//')

    try {
        const url = new URL(src)

        return url.protocol === 'https:' && OPTIMIZED_HOST.test(url.hostname)
    }
    catch {
        return false
    }
}

/**
 * Fills its parent (which must be `relative` with a size or aspect ratio)
 * with an image from a CMS image field, or a neutral placeholder when empty.
 */
export const CmsImage: FC<CmsImageProps> = ({ src, alt, sizes, className, preload = false }): ReactNode => {
    if (!src) {
        return (
            <div
                role={alt ? 'img' : undefined}
                aria-label={alt || undefined}
                className={cn('absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground/50', className)}
            >
                <HardHat className='size-10' aria-hidden='true' />
            </div>
        )
    }

    return (
        <Image
            src={src}
            alt={alt ?? ''}
            fill
            sizes={sizes}
            preload={preload}
            unoptimized={!canOptimize(src)}
            className={cn('object-cover', className)}
        />
    )
}
